/**
 * scheduleParser.ts
 * Client-side NLP utilities for the Schedules feature.
 * 
 * - Real-time regex-based token highlighting (instant, no API)
 * - AI-powered structured extraction on submit (accurate, async)
 * - Cron ↔ human-readable conversion helpers
 */

import { ParsedScheduleToken, ParsedSchedule } from '../types';

// ─── Token Highlighting (Real-Time, No API) ───

/** Time/frequency patterns to highlight as trigger tokens */
const TRIGGER_PATTERNS = [
  // Frequencies
  /\b(every\s+(day|morning|evening|night|week|weekday|weekend|month|year|hour|minute))\b/gi,
  /\b(every\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday))\b/gi,
  /\b(daily|weekly|monthly|yearly|hourly)\b/gi,
  // Specific times
  /\b(at\s+\d{1,2}(?::\d{2})?\s*(?:am|pm|AM|PM)?)\b/gi,
  // Relative dates
  /\b(every\s+\d+\s+(?:minute|hour|day|week|month)s?)\b/gi,
  // Days of week standalone
  /\b(on\s+(?:monday|tuesday|wednesday|thursday|friday|saturday|sunday)s?)\b/gi,
  // Morning/afternoon/evening
  /\b(in\s+the\s+(?:morning|afternoon|evening))\b/gi,
];

/** Action keywords to highlight */
const ACTION_PATTERNS = [
  /\b(sync|send|draft|create|read|fetch|update|delete|move|copy|share|schedule|email|write|compose|post|check|review|summarize|notify|remind)\b/gi,
];

/** Target/app keywords to highlight */
const TARGET_PATTERNS = [
  /\b(gmail|google\s+sheets?|google\s+docs?|google\s+calendar|google\s+drive|notion|slack|github|pull\s+requests?|prs?|spreadsheet|database|inbox|drafts?)\b/gi,
];

/**
 * Highlight scheduling tokens in real-time as the user types.
 * Returns an array of ParsedScheduleToken with type annotations.
 */
export const highlightScheduleTokens = (text: string): ParsedScheduleToken[] => {
  if (!text.trim()) return [];

  // Collect all matches with their positions
  const matches: { start: number; end: number; type: ParsedScheduleToken['type'] }[] = [];

  const findMatches = (patterns: RegExp[], type: ParsedScheduleToken['type']) => {
    for (const pattern of patterns) {
      const regex = new RegExp(pattern.source, pattern.flags);
      let match;
      while ((match = regex.exec(text)) !== null) {
        matches.push({
          start: match.index,
          end: match.index + match[0].length,
          type,
        });
      }
    }
  };

  findMatches(TRIGGER_PATTERNS, 'trigger');
  findMatches(ACTION_PATTERNS, 'action');
  findMatches(TARGET_PATTERNS, 'target');

  // Sort by start position
  matches.sort((a, b) => a.start - b.start);

  // Remove overlapping matches (keep the longer one)
  const filtered: typeof matches = [];
  for (const match of matches) {
    const last = filtered[filtered.length - 1];
    if (last && match.start < last.end) {
      // Overlapping — keep the longer one
      if (match.end - match.start > last.end - last.start) {
        filtered[filtered.length - 1] = match;
      }
    } else {
      filtered.push(match);
    }
  }

  // Build token array with plain text gaps
  const tokens: ParsedScheduleToken[] = [];
  let cursor = 0;

  for (const match of filtered) {
    if (match.start > cursor) {
      tokens.push({
        text: text.slice(cursor, match.start),
        type: 'plain',
        startIndex: cursor,
        endIndex: match.start,
      });
    }
    tokens.push({
      text: text.slice(match.start, match.end),
      type: match.type,
      startIndex: match.start,
      endIndex: match.end,
    });
    cursor = match.end;
  }

  if (cursor < text.length) {
    tokens.push({
      text: text.slice(cursor),
      type: 'plain',
      startIndex: cursor,
      endIndex: text.length,
    });
  }

  return tokens;
};

// ─── AI-Powered Structured Parsing (On Submit) ───

/**
 * Send the natural language schedule text to the AI for structured extraction.
 * Returns a ParsedSchedule with trigger info, actions, and a generated name.
 */
export const parseScheduleWithAI = async (text: string): Promise<ParsedSchedule> => {
  // @ts-ignore
  const apiKey = process.env.OPENROUTER_API_KEY || process.env.VITE_OPENROUTER_API_KEY || (window as any).OPENROUTER_API_KEY || '';
  if (!apiKey) throw new Error('OPENROUTER_API_KEY not found');

  const systemPrompt = `You are a schedule parser for Neura AI. Given a natural language schedule request, extract:

1. **trigger**: The timing/frequency of the schedule
   - type: "cron" (for time-based), "webhook" (for event-based), "event" (for conditional)
   - cron_expression: A valid 5-field cron expression (minute hour day-of-month month day-of-week)
   - human_readable: A clean human-readable description of when it runs

2. **actions**: Array of tool actions to perform, each with:
   - tool_name: The most likely MCP tool name (e.g., "list_emails", "create_doc", "send_email", "get_events", "read_sheet_values", "create_event")
   - args: Extracted parameters as key-value pairs
   - description: Human-readable description of the action

3. **name**: A short, descriptive name for this workflow (max 50 chars)

CRITICAL RULES FOR SCHEDULING ACTION GENERATION:
- Do NOT generate sending or writing steps (like 'send_email', 'compose_email', or 'create_doc') with placeholder or dummy parameters (e.g., 'user@example.com') when the user asks to "summarize", "check", or "retrieve" emails, spreadsheets, calendar events, etc.
- For requests like "give the summary of my emails daily", "summarize my inbox", or "check my calendar daily", generate ONLY the querying/retrieval steps (e.g., 'list_emails' or 'list_events'). The system will execute the query and automatically log and display the output directly to the user. Do not forward the output unless they explicitly ask to send/forward it to a specific external email address.

Common cron patterns:
- "Every day at 8 AM" → "0 8 * * *"
- "Every Monday at 9 AM" → "0 9 * * 1"
- "Every Friday at 5 PM" → "0 17 * * 5"
- "Every weekday at 9 AM" → "0 9 * * 1-5"
- "Every hour" → "0 * * * *"

Respond ONLY with valid JSON matching this exact structure:
{
  "trigger": { "type": "cron", "cron_expression": "...", "human_readable": "..." },
  "actions": [{ "tool_name": "...", "args": {}, "description": "..." }],
  "name": "..."
}`;

  const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
      'HTTP-Referer': window.location.origin,
      'X-Title': 'Neura AI',
    },
    body: JSON.stringify({
      model: 'openai/gpt-oss-120b:free',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: text },
      ],
      temperature: 0.1,
      max_tokens: 1024,
      response_format: { type: 'json_object' },
    }),
  });

  if (!response.ok) {
    throw new Error(`Schedule parsing failed: ${response.status}`);
  }

  const data = await response.json();
  const content = data.choices?.[0]?.message?.content || '{}';

  try {
    const parsed = JSON.parse(content);
    // Validate minimal structure
    if (!parsed.trigger || !parsed.actions || !parsed.name) {
      throw new Error('Incomplete parse result');
    }
    return parsed as ParsedSchedule;
  } catch (e) {
    console.error('[ScheduleParser] Failed to parse AI response:', content);
    // Return a best-effort fallback
    return {
      trigger: {
        type: 'cron',
        cron_expression: '0 9 * * *',
        human_readable: 'Every day at 9:00 AM',
      },
      actions: [{
        tool_name: 'unknown_action',
        args: {},
        description: text,
      }],
      name: text.slice(0, 50),
    };
  }
};

// ─── Cron ↔ Human-Readable Conversion ───

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Convert a cron expression to human-readable text */
export const cronToHumanReadable = (cron: string): string => {
  if (!cron) return 'No schedule set';

  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron; // Return raw if invalid

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Format the time
  const formatTime = (h: string, m: string): string => {
    if (h === '*' || m === '*') return '';
    const hourNum = parseInt(h, 10);
    const minNum = parseInt(m, 10);
    const period = hourNum >= 12 ? 'PM' : 'AM';
    const displayHour = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum;
    return `${displayHour}:${minNum.toString().padStart(2, '0')} ${period}`;
  };

  const time = formatTime(hour, minute);

  // Every minute
  if (minute === '*' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return 'Every minute';
  }

  // Every hour
  if (minute !== '*' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return `Every hour at :${minute.padStart(2, '0')}`;
  }

  // Daily
  if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*' && time) {
    return `Every day at ${time}`;
  }

  // Weekdays
  if (dayOfWeek === '1-5' && time) {
    return `Every weekday at ${time}`;
  }

  // Weekends
  if ((dayOfWeek === '0,6' || dayOfWeek === '6,0') && time) {
    return `Every weekend at ${time}`;
  }

  // Specific day of week
  if (dayOfWeek !== '*' && dayOfMonth === '*' && month === '*') {
    const days = dayOfWeek.split(',').map(d => DAYS_OF_WEEK[parseInt(d, 10)] || d);
    return `Every ${days.join(', ')}${time ? ` at ${time}` : ''}`;
  }

  // Specific day of month
  if (dayOfMonth !== '*' && dayOfWeek === '*' && month === '*' && time) {
    const suffix = (d: number) => {
      if (d >= 11 && d <= 13) return 'th';
      switch (d % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    const dayNum = parseInt(dayOfMonth, 10);
    return `Monthly on the ${dayNum}${suffix(dayNum)} at ${time}`;
  }

  // Fallback: return a generic description
  return `Cron: ${cron}`;
};

/** Check if text appears to contain scheduling intent */
export const hasScheduleIntent = (text: string): boolean => {
  const lower = text.toLowerCase().trim();
  // Must start with /schedule command or contain strong scheduling keywords
  if (lower.startsWith('/schedule')) return true;
  if (lower.startsWith('/routine')) return true;

  // Check for scheduling pattern combinations
  const hasFrequency = /\b(every|daily|weekly|monthly|hourly|recurring)\b/i.test(text);
  const hasAction = /\b(sync|send|draft|create|fetch|update|check|remind|notify)\b/i.test(text);
  return hasFrequency && hasAction;
};

/** Strip the /schedule or /routine prefix from user input */
export const stripScheduleCommand = (text: string): string => {
  if (typeof text !== 'string') return '';
  return text.replace(/^\/(schedule|routine)\s*/i, '').trim();
};
